import { CONSOLE_DENSITY, TOUCH_MEDIA, pxToRem, BUTTON_LINE_HEIGHT, capCentreLift, type ControlStep } from './density';

/**
 * The GEOMETRY of the shared MUI controls: heights, padding, insets, the
 * spacing inside cards, dialogs, menus and list rows. Colour, type and shape
 * are each theme's own (createAdminTheme, createAudienceTheme); the sizes are
 * not, so a 44px button means one thing in the console and on a fan's phone.
 *
 * Everything here is written in the CONSOLE form: the desktop value, with the
 * touch value in a `[TOUCH_MEDIA]` block (below the `sm` breakpoint). Admin and
 * desktop use it as it is. The audience app is a phone app, touch at every
 * width, so it passes each piece through `flattenTouch`, which folds the touch
 * block into the base. One source, two densities, no second copy of a number.
 */

const d = CONSOLE_DENSITY;
const touch = TOUCH_MEDIA;

type Style = Record<string, unknown>;

const isPlainObject = (v: unknown): v is Style => !!v && typeof v === 'object' && !Array.isArray(v);

const deepMerge = (base: Style, over: Style): Style => {
  const out: Style = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = isPlainObject(v) && isPlainObject(out[k]) ? deepMerge(out[k] as Style, v) : v;
  }
  return out;
};

/**
 * Fold every `[TOUCH_MEDIA]` block into its parent, at any depth, touch values
 * winning. A style function is wrapped so its result is folded too.
 */
export function flattenTouch<T>(style: T): T {
  if (typeof style === 'function') {
    return ((...args: unknown[]) => flattenTouch((style as (...a: unknown[]) => unknown)(...args))) as T;
  }
  if (Array.isArray(style)) return style.map(flattenTouch) as T;
  if (!isPlainObject(style)) return style;
  const out: Style = {};
  let touchBlock: unknown;
  for (const [k, v] of Object.entries(style)) {
    if (k === touch) touchBlock = v;
    else out[k] = flattenTouch(v);
  }
  return (isPlainObject(touchBlock) ? deepMerge(out, flattenTouch(touchBlock)) : out) as T;
}

export const CONTROL_STEPS: ControlStep[] = ['sm', 'md', 'lg'];

// A Button step: fixed height, horizontal padding only. The label is
// centred by the Button's own inline-flex, so vertical padding adds nothing
// but a way for the height to drift (it did: mobile medium buttons came out
// at 46px, not 44, because 10px + 10px + a 26px line box is 46).
export const buttonStep = (step: ControlStep) => ({
  height: d.control[step],
  minHeight: d.control[step],
  minWidth: d.buttonMinWidth[step],
  padding: `0 ${d.buttonPaddingX[step]}px`,
  fontSize: pxToRem(d.buttonFontSize[step]),
  lineHeight: BUTTON_LINE_HEIGHT,
  // Lifted onto the label's capitals (capCentreLift): centred on the line
  // box, an icon reads ~0.9px low beside 13px Rubik. `translate` moves the
  // glyph only; the button and its layout stay put.
  '& .MuiButton-startIcon > *:first-of-type, & .MuiButton-endIcon > *:first-of-type': {
    fontSize: d.buttonIcon[step],
    width: d.buttonIcon[step],
    height: d.buttonIcon[step],
    translate: `0 -${capCentreLift(d.buttonFontSize[step], d.buttonFontSize[step] * BUTTON_LINE_HEIGHT)}px`,
  },
  [touch]: {
    height: d.touchControl[step],
    minHeight: d.touchControl[step],
    minWidth: d.touchButtonMinWidth[step],
    padding: `0 ${d.touchButtonPaddingX[step]}px`,
    fontSize: pxToRem(d.touchButtonFontSize[step]),
    '& .MuiButton-startIcon > *:first-of-type, & .MuiButton-endIcon > *:first-of-type': {
      fontSize: d.touchButtonIcon[step],
      width: d.touchButtonIcon[step],
      height: d.touchButtonIcon[step],
      translate: `0 -${capCentreLift(d.touchButtonFontSize[step], d.touchButtonFontSize[step] * BUTTON_LINE_HEIGHT)}px`,
    },
  },
});

// An IconButton step: sized by its BOX, not by padding around whatever glyph
// the caller passed. `min*` rather than a fixed size so content bigger than
// the step (an Avatar in the user menu, an icon + count in the lineup) still
// fits; the side padding is what makes an icon-only button exactly square.
// A glyph wrapped in a Badge (the notification bell) is sized too, or it
// keeps Lucide's 24px default beside 20px neighbours.
export const iconPad = (height: number, step: ControlStep) => Math.max(0, (height - d.icon[step]) / 2);
/** The same at touch width, where both the box and the glyph are bigger. */
export const touchIconPad = (step: ControlStep) => Math.max(0, (d.touchControl[step] - d.touchIcon[step]) / 2);
const iconGlyph = '& .MuiSvgIcon-root, & > svg, & > .MuiBadge-root > svg';
export const iconButtonStep = (step: ControlStep) => ({
  minWidth: d.control[step],
  minHeight: d.control[step],
  padding: `0 ${iconPad(d.control[step], step)}px`,
  [iconGlyph]: {
    fontSize: pxToRem(d.icon[step]),
    width: d.icon[step],
    height: d.icon[step],
  },
  [touch]: {
    minWidth: d.touchControl[step],
    minHeight: d.touchControl[step],
    padding: `0 ${touchIconPad(step)}px`,
    [iconGlyph]: {
      fontSize: pxToRem(d.touchIcon[step]),
      width: d.touchIcon[step],
      height: d.touchIcon[step],
    },
  },
});

// `edge="start|end"` pulls an IconButton outward by its padding so the GLYPH
// lines up with the content edge. MUI hardcodes those offsets for its own
// padding (-12 / -3), which no longer describe these boxes.
export const iconButtonEdgeVariants = (['small', 'medium', 'large'] as const).flatMap((size) => {
  const step: ControlStep = size === 'small' ? 'sm' : size === 'medium' ? 'md' : 'lg';
  return (['start', 'end'] as const).map((edge) => {
    const side = edge === 'start' ? 'marginLeft' : 'marginRight';
    return {
      props: { size, edge },
      style: {
        [side]: -iconPad(d.control[step], step),
        [touch]: { [side]: -touchIconPad(step) },
      },
    };
  });
});

// Dropdowns: Menu, the Select's list and the Autocomplete list share one row
// (MenuGeometry in density.ts). Rows sit `inset` in from the surface with
// concentric corners, and their text starts on `textInset`, the field's own
// text inset, so a Select's options line up with the value shown above them.
const m = d.menu;
export const MENU_BORDER = 1;
export const MENU_LINE = m.lineHeight;
const menuItemPadX = m.textInset - MENU_BORDER - m.inset;
const menuText = (fontSize: number, lineHeight: number) => ({ fontSize: pxToRem(fontSize), lineHeight: `${lineHeight}px` });
export const menuItem = {
  minHeight: d.control.md,
  padding: `${(d.control.md - MENU_LINE) / 2}px ${menuItemPadX}px`,
  margin: `0 ${m.inset}px`,
  borderRadius: m.radius - m.inset,
  ...menuText(m.fontSize, m.lineHeight),
  [touch]: {
    minHeight: d.touchControl.md,
    paddingTop: (d.touchControl.md - m.touchLineHeight) / 2,
    paddingBottom: (d.touchControl.md - m.touchLineHeight) / 2,
    ...menuText(m.touchFontSize, m.touchLineHeight),
    '& .MuiListItemIcon-root': {
      minWidth: m.touchIcon + m.iconGap,
      '& svg': { width: m.touchIcon, height: m.touchIcon, translate: `0 -${capCentreLift(m.touchFontSize, m.touchLineHeight)}px` },
    },
    '& .MuiListItemText-primary': menuText(m.touchFontSize, m.touchLineHeight),
  },
  // A leading icon is a 16px glyph one gap from its label, not MUI's 20px
  // glyph in a 36px channel.
  '& .MuiListItemIcon-root': {
    minWidth: m.icon + m.iconGap,
    // Lifted onto the label's capitals, like a Button's icon (capCentreLift).
    '& svg': { width: m.icon, height: m.icon, translate: `0 -${capCentreLift(m.fontSize, m.lineHeight)}px` },
  },
  '& .MuiListItemText-root': { marginTop: 0, marginBottom: 0 },
  '& .MuiListItemText-primary': menuText(m.fontSize, m.lineHeight),
};
/** A dropdown section header; the colour is the theme's. */
export const menuSubheader = {
  lineHeight: `${m.subheaderHeight}px`,
  paddingLeft: m.textInset - MENU_BORDER,
  paddingRight: m.textInset - MENU_BORDER,
  fontSize: pxToRem(12),
  fontWeight: 500,
  letterSpacing: 'normal',
  textTransform: 'none' as const,
  // Background stays MUI's paper: an Autocomplete group label is sticky,
  // and rows would scroll visibly under a transparent one.
};
export const menuList = {
  paddingTop: m.inset,
  paddingBottom: m.inset,
  '& .MuiDivider-root': { marginTop: m.inset, marginBottom: m.inset },
};

// Cards (CardGeometry): the inset their content and actions take rides a
// variable set on the Card, with its touch value, so a CardContent's own
// `sx={{ p }}` still wins plainly, as with the table cells.
const cardPad = `var(--Showliner-cardPadding, ${d.card.padding}px)`;
export const cardPaddingVars = {
  '--Showliner-cardPadding': `${d.card.padding}px`,
  [touch]: { '--Showliner-cardPadding': `${d.card.touchPadding}px` },
};
export const cardContent = {
  padding: cardPad,
  // MUI gives the last CardContent 24px at the bottom.
  '&:last-child': { paddingBottom: cardPad },
};
export const cardHeader = { padding: cardPad };
// Actions share the card's inset and sit one inset below the content
// (CardContent's bottom padding), buttons 8px apart as in a dialog.
// MUI's own is 8px all round.
export const cardActions = { padding: `0 ${cardPad} ${cardPad}` };

// Dialogs (DialogGeometry in density.ts): one inset round everything and
// one gap between title, content and actions. The space under a title
// and above the buttons is set HERE; a dialog that nudges `pb` / `pt` on
// its parts is fighting the theme, which is how a dozen of them came to
// disagree.
const dg = d.dialog;
// At touch width, MUI's 32px margin leaves a 390px phone a 326px
// dialog. Not on a full-screen one (ResponsiveDetailDialog on a
// phone), which has no margin and must keep none.
export const dialogPaperMargin = {
  [touch]: {
    '&:not(.MuiDialog-paperFullScreen)': {
      margin: dg.touchMargin,
      maxHeight: `calc(100% - ${2 * dg.touchMargin}px)`,
      '&.MuiDialog-paperFullWidth': { width: `calc(100% - ${2 * dg.touchMargin}px)` },
    },
  },
};
export const dialogTitle = {
  padding: `${dg.padding}px ${dg.padding}px ${dg.gap}px`,
  lineHeight: `${dg.titleLine}px`,
  [touch]: { padding: `${dg.touchPadding}px ${dg.touchPadding}px ${dg.gap}px` },
  // Make room for a DialogCloseButton beside the title, whichever
  // side of it in the markup the button sits.
  '&:has(+ .Showliner-dialogClose), .Showliner-dialogClose + &': {
    paddingRight: dg.padding + d.control.md,
    [touch]: { paddingRight: dg.touchPadding + d.touchControl.md },
  },
};
export const dialogContent = {
  // Under a title MUI already drops the top padding (the title's
  // bottom padding is the gap); above actions the actions' top
  // padding is, so only a last child keeps its bottom inset.
  padding: dg.padding,
  '&:not(:last-child)': { paddingBottom: 0 },
  // MUI's rule only fires when content directly follows the title;
  // a DialogCloseButton between them would put 20px more under it.
  '.MuiDialogTitle-root + .Showliner-dialogClose + &:not(.MuiDialogContent-dividers)': { paddingTop: 0 },
  [touch]: { padding: dg.touchPadding },
  '&.MuiDialogContent-dividers': {
    padding: `${dg.gap}px ${dg.padding}px`,
    [touch]: { padding: `${dg.gap}px ${dg.touchPadding}px` },
  },
};
export const dialogActions = {
  padding: `${dg.gap}px ${dg.padding}px ${dg.padding}px`,
  [touch]: { padding: `${dg.gap}px ${dg.touchPadding}px ${dg.touchPadding}px` },
};

// Segmented toggle (ToggleButtonGroup): a track on the `md` step with its
// segments inset by `TOGGLE_INSET`. The corner radius is the theme's (a
// console rounds 6px, the audience app is a pill), so it is a parameter;
// a segment's radius is the track's less the inset, so the curves stay
// concentric.
export const TOGGLE_INSET = 2;
export const toggleGroupTrack = (fullWidth: boolean | undefined) => ({
  boxSizing: 'border-box' as const,
  // Hug the segments. The group is inline-flex, which does NOT stop a
  // column flex parent stretching it: the track spanned the column
  // with its segments bunched at the left. A width that isn't `auto`
  // opts out of the stretch. `fullWidth` still spans and splits.
  width: fullWidth ? '100%' : 'fit-content',
  height: d.control.md,
  padding: TOGGLE_INSET,
  gap: TOGGLE_INSET,
  border: 'none',
  [touch]: { height: d.touchControl.md },
});
export const toggleButtonBox = {
  height: d.control.md,
  padding: `0 ${d.buttonPaddingX.md}px`,
  gap: 6,
  border: 0,
  fontSize: pxToRem(d.buttonFontSize.md),
  '& svg': { width: d.buttonIcon.md, height: d.buttonIcon.md, flexShrink: 0 },
  [touch]: {
    height: d.touchControl.md,
    fontSize: pxToRem(d.touchButtonFontSize.md),
    '& svg': { width: d.touchButtonIcon.md, height: d.touchButtonIcon.md },
  },
};
